import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Colors, Sizes } from '@/components/ui/types';

import {
  RangeSliderIndicatorProps,
  RangeSliderMeasureProps,
  RangeSliderProps,
  RangeSliderValueProps,
} from './types';

const colorClasses: Record<Colors, string> = {
  default: '',
  neutral: 'range-neutral',
  primary: 'range-primary',
  secondary: 'range-secondary',
  accent: 'range-accent',
  info: 'range-info',
  success: 'range-success',
  warning: 'range-warning',
  error: 'range-error',
};

const sizeMap: Record<Sizes, string> = {
  xs: 'range-xs',
  sm: 'range-sm',
  md: 'range-md',
  lg: 'range-lg',
  xl: 'range-xl',
};

const RangeSliderBase = forwardRef<HTMLInputElement, RangeSliderProps>(
  (
    { className, size = 'md', color = 'default', min = 0, max = 100, ...props },
    ref,
  ) => {
    const classes = twMerge(
      'range',
      colorClasses[color],
      sizeMap[size],
      className,
    );

    return (
      <input
        ref={ref}
        type="range"
        min={min}
        max={max}
        className={classes}
        {...props}
      />
    );
  },
);

const Measure = ({ className, children }: RangeSliderMeasureProps) => (
  <div
    className={twMerge(
      'flex w-full justify-between px-2.5 mt-2 text-xs',
      className,
    )}
  >
    {children}
  </div>
);

const Indicator = ({ className }: RangeSliderIndicatorProps) => (
  <span className={className}>|</span>
);

const Value = ({ className, value }: RangeSliderValueProps) => (
  <span className={className}>{value}</span>
);

RangeSliderBase.displayName = 'RangeSlider';
Measure.displayName = 'RangeSlider.Measure';
Indicator.displayName = 'RangeSlider.Indicator';
Value.displayName = 'RangeSlider.Value';

export const RangeSlider = Object.assign(RangeSliderBase, {
  Measure,
  Indicator,
  Value,
});
